<template lang="pug">
UpsertPage(
  title="E-mail settings"
  @save="validateAndSave"
  :pending="isSaving"
  :canSave="canSave"
  hide-cancel
)
  BriqPanel.email-settings
    BriqMultilanguageWarningModal(
      v-if="hasMultipleLanguages"
      v-show="showWarningModal"
      :defaultModel="emailSettings"
      :translationInfo="emailSettings.TranslationInfo"
      :translatableProperties="translatableProperties"
      @close="showWarningModal = false" 
      @onSubmit="save"
      )
    .translation-header(v-if="hasMultipleLanguages")
      BriqButton.translation-button(
        small
        variant="secondary"
        @click="toggleTranslation"
        data-test-id="translation-button"
        )
        .flex
          .translation-icon.mr-XXS
            BriqIcon(:name="`icon-input-${toggleTranslationIcon}-icon`") 
          .translation-text {{ toggleTranslationText }}
    BriqMultilanguage(
      v-model="activeCulture"
      :translationFields="translationFields"
      :cultures="emailSettings.TranslationInfo.AvailableCultures"
      :showSecondColumn="shouldShowMultilanguage"
      :defaultCulture="defaultCultureDisplayName"
      )
      EmailSettingsFields(
        ref="emailSettingsFields"
        :emailSettings="emailSettings"
        :placeholders="placeholders"
        :showSecondColumn="false"
        @canSave="canFormBeSaved"
        @hasHTMLError="hasHTMLError"
        )
      template(v-slot:secondary)
        EmailSettingsFields(
          v-if="activeCulture"
          :emailSettings="translationModel"
          :placeholders="placeholders"
          :showSecondColumn="shouldShowMultilanguage"
          @hasHTMLError="hasHTMLError"
          )
</template>

<script>
import UpsertPage from '@v/components/UpsertPage.vue'
import EmailSettingsFields from '@v/settings/application//emails/EmailSettingsFields.vue'
import BriqMultilanguage from '@/vue/components/BriqMultilanguage.vue'
import BriqMultilanguageWarningModal from '@/vue/components/BriqMultilanguageWarningModal.vue'
import completeTranslationInfo from '@/vue/util/completeTranslationInfo.js'
import { emptyTranslatableFieldsExist } from '@v/util/emptyTranslatableFields.js'

export default {
  name: 'EmailSettingsPage',
  components: {
    UpsertPage,
    BriqMultilanguage,
    EmailSettingsFields,
    BriqMultilanguageWarningModal,
  },
  props: {
    emailSettings: Object,
    placeholders: Object,
    isSaving: Boolean,
    enableMultilanguage: Boolean, // feature flag
  },
  data() {
    return {
      canSave: false,
      activeCulture: this.emailSettings.TranslationInfo?.AvailableCultures?.[0],
      showTranslations: false,
      showWarningModal: false,
      unsafeHTMLCount: 0
    }
  },
  computed: {
    hasMultipleLanguages() {
      return (
        this.enableMultilanguage && !!this.emailSettings.TranslationInfo?.AvailableCultures?.length
      )
    },
    toggleTranslationText() {
      const what = this.showTranslations ? 'Hide' : 'Show'
      return what + ' translations'
    },
    toggleTranslationIcon() {
      return this.showTranslations ? 'eye-crossed' : 'eye-open'
    },
    shouldShowMultilanguage() {
      return this.enableMultilanguage && this.showTranslations
    },
    defaultCulture() {
      return this.emailSettings.TranslationInfo?.DefaultCulture
    },
    defaultCultureDisplayName() {
      return this.defaultCulture ? this.defaultCulture?.DisplayName : ''
    },
    translationFields() {
      // todo: refactor to make it work with model field name instead of label
      return ['Custom text confirmation e-mail', 'Custom text reminder e-mail']
    },
    translatableProperties() {
      return [
        {
          label: 'Custom text confirmation e-mail',
          key: 'CustomMailText',
        },
        {
          label: 'Custom text reminder e-mail',
          key: 'CustomReminderMailText',
        },
      ]
    },
    translationModel() {
      if (!this.activeCulture) return null
      const key = this.activeCulture.Name
      return this.emailSettings.TranslationInfo.Translations[key]
    },
  },
  methods: {
    canFormBeSaved(value) {
      this.canSave = value
    },
    toggleTranslation() {
      this.showTranslations = !this.showTranslations
    },
    hasHTMLError(val) {
      if (val === true) this.unsafeHTMLCount++
      if (val === false) this.unsafeHTMLCount--
    },
    validateAndSave() {
      if (
        this.hasMultipleLanguages &&
        emptyTranslatableFieldsExist(
          this.emailSettings,
          this.emailSettings.TranslationInfo,
          this.translatableProperties,
        )
      ) {
        this.showWarningModal = true
      } else {
        this.save()
      }
    },
    save() {
      if (this.unsafeHTMLCount > 0) {
        this.$notifyWarning('Unable to save: your HTML might contain vulnerabilities.')
      } else {
        this.$emit('save')
      }
    },
  },
  beforeMount() {
    completeTranslationInfo(this.emailSettings)
  },
}
</script>

<style lang="stylus">
.email-settings
 .briq-form-element, h2
    margin-bottom 24px

  .translation-header
    display flex
    width 100%
    justify-content space-between
    flex-direction row-reverse

    .translation-button
      margin-bottom 24px
</style>
